import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DentalVisitAnswers from '../components/DentalVisitAnswers';
import { visitFAQs } from '../data/visit';
import { generateFAQSchema } from '../utils/seo';
import BookAppointmentLink from '../components/BookAppointmentLink';
import Footer from '../components/Footer';
import PricingSections from '../components/PricingSections';

const OurServices: React.FC = () => {
  const includedServices = [
    'Doctor exam (complimentary when you proceed with recommended care; $50 if you do not proceed)',
    'Pre-anesthetic bloodwork (CBC/chemistry), billed separately',
    'IV catheter, fluids, anesthesia & monitoring',
    'Digital dental X-rays (panoramic/intraoral)',
    'Scaling, charting, polishing',
    'Extractions at published per-tooth rates when needed',
    'Aftercare instructions; medications billed separately if required',
    'Treatment plan with itemized pricing before procedures',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-soft-gray to-white">
      {/* Header with Hero Image */}
      <section className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
        <img
          src="/Chomp-Front.png"
          alt="Chomp Pet Dental Clinic Front"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="container-custom text-center px-4">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg font-nantes">
              Dog &amp; Cat Dental Services in Fort Lauderdale
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 font-medium max-w-3xl mx-auto drop-shadow-md">
              Veterinary dental care that's simple, transparent, and effective
            </p>
          </div>
        </div>
      </section>

      <Helmet><script type="application/ld+json">{JSON.stringify(generateFAQSchema(visitFAQs))}</script></Helmet>
      <DentalVisitAnswers />
      <section className="bg-gray-50 py-10">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl font-bold text-primary mb-4">Dental services for dogs and cats</h2>
          <ul className="space-y-3 text-lg">
            <li><Link className="text-brand-teal underline" to="/services/routine-dental-cleaning">Routine dental cleaning</Link> — examination, X-rays, scaling, charting, and polishing under anesthesia.</li>
            <li><Link className="text-brand-teal underline" to="/services/moderate-dental-treatment">Mild to moderate dental treatment</Link> — cleaning with extractions and periodontal treatment when needed.</li>
            <li><Link className="text-brand-teal underline" to="/services/advanced-dental-surgery">Advanced dental surgery</Link> — discuss complex dental needs with our veterinary team.</li>
          </ul>
        </div>
      </section>

      {/* Transparent Flat Pricing */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-6">
              <i className="fas fa-dollar-sign text-3xl text-yellow-600"></i>
              <h2 className="text-3xl md:text-4xl font-bold text-primary">
                Itemized Dental Pricing
              </h2>
            </div>
          </div>
          
          <div className="max-w-6xl mx-auto">
            {/* What's Included */}
            <div className="mb-16">
              <h3 className="text-2xl font-bold text-center text-primary mb-8">
                Your treatment plan may include:
              </h3>
              <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-4">
                {includedServices.map((service, index) => (
                  <div key={index} className="flex items-center gap-3 bg-card-bg p-4 rounded-lg">
                    <i className={`fas fa-check text-lg ${
                      index % 4 === 0 ? 'text-teal-600' : 
                      index % 4 === 1 ? 'text-yellow-500' :
                      index % 4 === 2 ? 'text-orange-500' : 'text-red-500'
                    }`}></i>
                    <span className="text-gray-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <PricingSections showTitles />

            <div className="text-center mt-8">
              <p className="text-gray-600 italic">
                *Final cost depends on your pet&apos;s exam findings; we&apos;ll confirm an itemized estimate before treatment.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-r from-brand-teal to-orange-500 text-white section-padding">
        <div className="container-custom text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="flex gap-1">
              <i className="fas fa-paw text-3xl opacity-80"></i>
              <i className="fas fa-paw text-3xl opacity-60"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold">
              Let's Do This Right
            </h2>
          </div>
          
          <p className="text-2xl md:text-3xl mb-6 opacity-90">
            No stress. No games. Just real care that helps.
          </p>
          
          <p className="text-xl mb-12 opacity-80 max-w-4xl mx-auto">
            Now open in Fort Lauderdale. Book your appointment today.
          </p>
          
          <BookAppointmentLink 
            className="bg-white text-brand-teal px-12 py-6 rounded-lg font-bold text-2xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl inline-block"
          >
            BOOK NOW
          </BookAppointmentLink>
        </div>
      </section>

      {/* Additional Info */}
      <section className="bg-white py-16">
        <div className="container-custom text-center">
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <div className="flex items-center gap-3">
              <i className="fas fa-phone text-brand-teal text-xl"></i>
                              <span className="text-gray-700">Questions? Email info@chomp.vet</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fas fa-calendar text-brand-teal text-xl"></i>
              <span className="text-gray-700">Now Open! Book Now.</span>
            </div>
            <div className="flex items-center gap-3">
              <i className="fas fa-shield-alt text-brand-teal text-xl"></i>
              <span className="text-gray-700">Itemized estimate before treatment</span>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default OurServices;